/**
 * Global calendar (F1): meetings (recurrence materialized in window +
 * exceptions) + rehearsals, with FULL FILTER — church, venue, group, activity
 * kind, public/private and date range. All local-first: only what the grants
 * sync is rendered.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  buildCalendarEvents,
  type CalendarVisibility,
  filterCalendarEvents,
  getChurchesQuery,
  getGroupsQuery,
  getMeetingExceptionsQuery,
  getMeetingsQuery,
  getRehearsalsQuery,
  getVenuesQuery,
  groupEventsByDay,
  queryKeys,
  REHEARSAL_KIND,
} from "@agape/shared";
import type {
  ChurchGroupRecord,
  ChurchRecord,
  MeetingExceptionRecord,
  MeetingRecord,
  RehearsalRecord,
  VenueRecord,
} from "@agape/powersync";
import { CalendarDays, Repeat } from "@fleet/icons";

import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { TextField } from "@fleet/ui/text-field";
import { MEETING_KINDS, meetingKindLabel } from "./meetings-page";
import { useDomainQuery } from "../use-domain";

const DAY_MS = 86_400_000;

const pad = (n: number) => String(n).padStart(2, "0");

function toDateInput(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function CalendarPage() {
  const { t } = useI18n();

  const meetings = useDomainQuery<MeetingRecord>(
    (uid) => queryKeys.domain.meetings(uid),
    (db) => getMeetingsQuery(db),
  );
  const exceptions = useDomainQuery<MeetingExceptionRecord>(
    (uid) => queryKeys.domain.meetingExceptions(uid),
    (db) => getMeetingExceptionsQuery(db),
  );
  const rehearsals = useDomainQuery<RehearsalRecord>(
    (uid) => queryKeys.domain.rehearsals(uid),
    (db) => getRehearsalsQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const venues = useDomainQuery<VenueRecord>(
    (uid) => queryKeys.domain.venues(uid),
    (db) => getVenuesQuery(db),
  );
  const groups = useDomainQuery<ChurchGroupRecord>(
    (uid) => queryKeys.domain.groups(uid),
    (db) => getGroupsQuery(db),
  );

  // ── Filters ───────────────────────────────────────────────────────────────
  const [from, setFrom] = createSignal(toDateInput(Date.now() - 7 * DAY_MS));
  const [to, setTo] = createSignal(toDateInput(Date.now() + 45 * DAY_MS));
  const [churchId, setChurchId] = createSignal("");
  const [venueId, setVenueId] = createSignal("");
  const [groupId, setGroupId] = createSignal("");
  const [kind, setKind] = createSignal("");
  const [visibility, setVisibility] = createSignal<CalendarVisibility>("all");

  const window = createMemo(() => ({
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- ISO suffix, not UI
    from: Date.parse(`${from()}T00:00:00`) || Date.now() - 7 * DAY_MS,
    to: (Date.parse(`${to()}T00:00:00`) || Date.now() + 45 * DAY_MS) + DAY_MS - 1,
  }));

  const events = createMemo(() =>
    buildCalendarEvents(
      {
        meetings: meetings.data ?? [],
        exceptions: exceptions.data ?? [],
        rehearsals: rehearsals.data ?? [],
      },
      window(),
    ),
  );
  const filtered = createMemo(() =>
    filterCalendarEvents(events(), {
      churchId: churchId() || undefined,
      venueId: venueId() || undefined,
      groupId: groupId() || undefined,
      kinds: kind() ? [kind()] : undefined,
      visibility: visibility(),
    }),
  );
  const days = createMemo(() => groupEventsByDay(filtered()));

  const churchName = (id: string | null) =>
    (churches.data ?? []).find((c) => c.id === id)?.name ?? "";
  const groupName = (id: string | null) => (groups.data ?? []).find((g) => g.id === id)?.name ?? "";
  const venueName = (id: string | null) => (venues.data ?? []).find((v) => v.id === id)?.name ?? "";
  const kindLabel = (k: string) =>
    k === REHEARSAL_KIND ? t(msg`Rehearsal`) : meetingKindLabel(t, k);

  return (
    <div class="space-y-4">
      <PageSubHeader title={t(msg`Calendar`)} />

      {/* ── Filtro completo ──────────────────────────────────────────────── */}
      <div
        class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-7"
        data-testid="calendar-filters"
      >
        <TextField
          type="date"
          value={from()}
          onInput={(e) => setFrom(e.currentTarget.value)}
          aria-label={t(msg`From`)}
          data-testid="calendar-from"
        />
        <TextField
          type="date"
          value={to()}
          onInput={(e) => setTo(e.currentTarget.value)}
          aria-label={t(msg`To`)}
          data-testid="calendar-to"
        />
        <div data-testid="calendar-filter-church">
          <Select
            options={[
              { value: "", label: t(msg`All churches`) },
              ...(churches.data ?? []).map((c) => ({ value: c.id, label: c.name ?? c.id })),
            ]}
            value={churchId()}
            onChange={setChurchId}
            aria-label={t(msg`Church`)}
          />
        </div>
        <div data-testid="calendar-filter-venue">
          <Select
            options={[
              { value: "", label: t(msg`All venues`) },
              ...(venues.data ?? []).map((v) => ({ value: v.id, label: v.name ?? v.id })),
            ]}
            value={venueId()}
            onChange={setVenueId}
            aria-label={t(msg`Venue`)}
          />
        </div>
        <div data-testid="calendar-filter-group">
          <Select
            options={[
              { value: "", label: t(msg`All groups`) },
              ...(groups.data ?? []).map((g) => ({ value: g.id, label: g.name ?? g.id })),
            ]}
            value={groupId()}
            onChange={setGroupId}
            aria-label={t(msg`Group`)}
          />
        </div>
        <div data-testid="calendar-filter-kind">
          <Select
            options={[
              { value: "", label: t(msg`All kinds`) },
              ...MEETING_KINDS.map((k) => ({ value: k, label: meetingKindLabel(t, k) })),
              { value: REHEARSAL_KIND, label: t(msg`Rehearsal`) },
            ]}
            value={kind()}
            onChange={setKind}
            aria-label={t(msg`Kind`)}
          />
        </div>
        <div data-testid="calendar-filter-visibility">
          <Select<CalendarVisibility>
            options={[
              { value: "all", label: t(msg`Public and private`) },
              { value: "public", label: t(msg`Public only`) },
              { value: "private", label: t(msg`Private only`) },
            ]}
            value={visibility()}
            onChange={setVisibility}
            aria-label={t(msg`Visibility`)}
          />
        </div>
      </div>

      {/* ── Agenda ───────────────────────────────────────────────────────── */}
      <Show
        when={days().length > 0}
        fallback={
          <p data-testid="calendar-empty" class="text-muted-foreground">
            {t(msg`Nothing scheduled for these filters.`)}
          </p>
        }
      >
        <div class="space-y-4" data-testid="calendar-agenda">
          <For each={days()}>
            {(day) => (
              <div class="space-y-1.5">
                <p class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {/* oxlint-disable-next-line lingui/no-unlocalized-strings -- sufijo ISO, no UI */}
                  {new Date(`${day.day}T12:00:00`).toLocaleDateString(undefined, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </p>
                <For each={day.events}>
                  {(event) => (
                    <Link
                      to={event.source === "meeting" ? "/meetings/$meetingId" : "/groups/$groupId"}
                      params={
                        event.source === "meeting"
                          ? { meetingId: event.sourceId }
                          : { groupId: event.groupId ?? "" }
                      }
                      class="block"
                    >
                      <Card
                        data-testid="calendar-event"
                        data-event-kind={event.kind}
                        data-event-church={event.churchId ?? ""}
                        class="transition-colors hover:border-primary/40"
                      >
                        <CardContent class="flex items-center gap-3 py-2.5">
                          <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                            <CalendarDays size={15} />
                          </span>
                          <div class="min-w-0 flex-1">
                            <p class="truncate text-sm font-medium">{event.title}</p>
                            <p class="truncate text-xs text-muted-foreground">
                              {[
                                kindLabel(event.kind),
                                churchName(event.churchId),
                                venueName(event.venueId),
                                groupName(event.groupId),
                                event.isPublic ? t(msg`Public`) : t(msg`Private`),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <div class="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                            <Show when={event.isRecurring}>
                              <Repeat size={13} aria-label={t(msg`Recurring`)} />
                            </Show>
                            <span>
                              {new Date(event.start).toLocaleTimeString(undefined, {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  )}
                </For>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
